import { Button, DatePicker, Segmented, Select } from 'antd';
import { ClearOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { FilterBar } from '@/components/ui/FilterBar';
import { HorizontalScroll } from '@/components/ui/HorizontalScroll';
import type { Dayjs } from 'dayjs';
import type {
  ConversationOverviewChannel,
  ConversationOverviewFlag
} from '@/api/chat/interfaces';
import {
  getConversationOverviewPeriods,
  type ConversationOverviewPeriod
} from './constants';

const { RangePicker } = DatePicker;

export type ConversationOverviewFilterValues = {
  period: ConversationOverviewPeriod;
  clientId?: string;
  channel?: ConversationOverviewChannel;
  flag?: ConversationOverviewFlag;
  humanHandoff?: boolean;
  technicalIssue?: boolean;
};

type TenantOption = { value: string; label: string };

type ConversationOverviewFiltersProps = {
  values: ConversationOverviewFilterValues;
  customRange: [Dayjs | null, Dayjs | null] | null;
  tenantOptions: TenantOption[];
  hasActiveFilters: boolean;
  onChange: (patch: Record<string, unknown>) => void;
  onClear: () => void;
};

/**
 * Filter bar of the conversation overview: period, tenant, channel, flag, handoff and technical issue.
 */
export function ConversationOverviewFilters({
  values,
  customRange,
  tenantOptions,
  hasActiveFilters,
  onChange,
  onClear
}: ConversationOverviewFiltersProps) {
  const { t } = useTranslation('admin');
  const periodOptions = useMemo(() => getConversationOverviewPeriods(t), [t]);

  return (
    <FilterBar
      actions={
        hasActiveFilters ? (
          <Button icon={<ClearOutlined />} onClick={onClear}>
            {t('conversationOverview.filters.clearAll')}
          </Button>
        ) : undefined
      }
    >
      <HorizontalScroll>
        <Segmented
          options={periodOptions}
          value={values.period}
          onChange={(value) => {
            if (value === 'custom') {
              onChange({ period: 'custom' });
              return;
            }
            onChange({ period: value, from: undefined, to: undefined, page: 1 });
          }}
        />
      </HorizontalScroll>
      {values.period === 'custom' && (
        <RangePicker
          style={{ flex: '1 1 240px', minWidth: 0, maxWidth: 320 }}
          value={customRange}
          onChange={(dates) => {
            if (!dates?.[0] || !dates?.[1]) {
              onChange({ from: undefined, to: undefined, page: 1 });
              return;
            }
            onChange({
              from: dates[0].startOf('day').toISOString(),
              to: dates[1].endOf('day').toISOString(),
              page: 1
            });
          }}
        />
      )}
      <Select
        allowClear
        placeholder={t('conversationOverview.filters.tenant')}
        style={{ flex: '1 1 200px', minWidth: 160 }}
        value={values.clientId}
        options={tenantOptions}
        onChange={(value) => onChange({ clientId: value, page: 1 })}
        showSearch
        optionFilterProp="label"
      />
      <Select
        allowClear
        placeholder={t('conversationOverview.filters.channel')}
        style={{ flex: '1 1 140px', minWidth: 130 }}
        value={values.channel}
        options={[
          { value: 'VOICE', label: t('conversationOverview.channel.call') },
          { value: 'WEBCHAT', label: t('conversationOverview.channel.chat') },
          { value: 'EMAIL', label: t('conversationOverview.channel.email') }
        ]}
        onChange={(value) => onChange({ channel: value, page: 1 })}
      />
      <Select
        allowClear
        placeholder={t('conversationOverview.filters.flag')}
        style={{ flex: '1 1 120px', minWidth: 120 }}
        value={values.flag}
        options={[
          { value: 'GREEN', label: t('conversationOverview.flag.green') },
          { value: 'RED', label: t('conversationOverview.flag.red') }
        ]}
        onChange={(value) => onChange({ flag: value, page: 1 })}
      />
      <Select
        allowClear
        placeholder={t('conversationOverview.filters.humanHandoff')}
        style={{ flex: '1 1 160px', minWidth: 150 }}
        value={
          values.humanHandoff === undefined ? undefined : values.humanHandoff ? 'true' : 'false'
        }
        options={[
          { value: 'true', label: t('conversationOverview.yes') },
          { value: 'false', label: t('conversationOverview.no') }
        ]}
        onChange={(value) =>
          onChange({
            humanHandoff: value === undefined ? undefined : value === 'true',
            page: 1
          })
        }
      />
      <Select
        allowClear
        placeholder={t('conversationOverview.filters.technicalIssue')}
        style={{ flex: '1 1 160px', minWidth: 150 }}
        value={
          values.technicalIssue === undefined
            ? undefined
            : values.technicalIssue
              ? 'true'
              : 'false'
        }
        options={[
          { value: 'true', label: t('conversationOverview.yes') },
          { value: 'false', label: t('conversationOverview.no') }
        ]}
        onChange={(value) =>
          onChange({
            technicalIssue: value === undefined ? undefined : value === 'true',
            page: 1
          })
        }
      />
    </FilterBar>
  );
}
